<template>
  <div class="themed-dt-field">
    <label
      v-if="label"
      class="field-label themed-dt-label"
      :for="triggerId"
    >
      {{ label }}<span
        v-if="required"
        class="themed-dt-required"
      >*</span>
    </label>

    <button
      :id="triggerId"
      type="button"
      :class="['themed-dt-trigger', { 'has-value': hasValue }]"
      :aria-label="label || 'Pick date and time'"
      @click="onOpen"
    >
      <span class="themed-dt-trigger-text">{{ display }}</span>
      <span
        class="material-symbols-rounded themed-dt-trigger-icon"
        aria-hidden="true"
      >
        {{ presentation === 'time' ? 'schedule' : 'event' }}
      </span>
      <button
        v-if="hasValue && clearable"
        type="button"
        class="themed-dt-clear"
        aria-label="Clear date"
        @click.stop="onClear"
      >
        <span
          class="material-symbols-rounded"
          aria-hidden="true"
        >close</span>
      </button>
    </button>

    <IonModal
      :is-open="open"
      class="themed-dt-modal"
      :backdrop-dismiss="true"
      @did-dismiss="open = false"
    >
      <div class="themed-dt-modal-inner">
        <IonDatetime
          :id="datetimeId"
          v-model="internalValue"
          :presentation="presentation"
          :hour-cycle="hourCycle"
          :first-day-of-week="0"
          :show-default-buttons="true"
          :show-clear-button="clearable"
          done-text="Set"
          cancel-text="Cancel"
          clear-text="Clear"
          color="primary"
          @ion-change="onChange"
          @ion-cancel="onCancel"
        />
      </div>
    </IonModal>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue"
import { IonModal, IonDatetime, type DatetimeChangeEventDetail } from "@ionic/vue"

type Presentation = "date-time" | "date" | "time" | "month" | "month-year" | "year"

interface Props {
  modelValue: string
  label?: string
  required?: boolean
  presentation?: Presentation
  hourCycle?: "h11" | "h12" | "h23" | "h24"
  clearable?: boolean
  placeholder?: string
}

const props = withDefaults(defineProps<Props>(), {
  label: "",
  required: false,
  presentation: "date-time",
  hourCycle: undefined,
  clearable: true,
  placeholder: "",
})

const emit = defineEmits<{ (e: "update:modelValue", value: string): void }>()

const id = `dt-${Math.random().toString(36).slice(2, 10)}`
const triggerId = `${id}-trigger`
const datetimeId = `${id}-picker`

const open = ref(false)
// Internal copy so the wheel/grid can scrub without firing updates on every tick.
const internalValue = ref<string>(props.modelValue || "")
watch(() => props.modelValue, (v) => { internalValue.value = v || "" })

const hasValue = computed(() => Boolean(props.modelValue))

const display = computed(() => {
  if (!props.modelValue) {
    return props.placeholder || defaultPlaceholder(props.presentation)
  }
  const date = parseLocal(props.modelValue)
  if (!date) return props.modelValue
  return formatFor(date, props.presentation)
})

function onOpen() {
  internalValue.value = props.modelValue || ""
  open.value = true
}

function onChange(e: CustomEvent<DatetimeChangeEventDetail>) {
  const raw = e.detail.value
  if (typeof raw !== "string") {
    if (raw == null) emit("update:modelValue", "")
    return
  }
  // Normalize to the same format the native datetime-local input emitted
  // ("YYYY-MM-DDTHH:mm") so consumers don't need to change their submit logic.
  emit("update:modelValue", raw.length >= 16 ? raw.slice(0, 16) : raw)
}

function onCancel() {
  open.value = false
}

function onClear() {
  emit("update:modelValue", "")
}

function parseLocal(value: string): Date | null {
  // The picker emits local-time ISO without a timezone, e.g. "2026-05-24T20:54".
  // new Date() interprets that as local — exactly what we want for display.
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

function formatFor(date: Date, presentation: Presentation): string {
  const opts: Intl.DateTimeFormatOptions = {}
  switch (presentation) {
    case "time":
      opts.timeStyle = "short"; break
    case "date":
      opts.dateStyle = "medium"; break
    case "month":
    case "month-year":
      opts.month = "long"; opts.year = "numeric"; break
    case "year":
      opts.year = "numeric"; break
    default:
      opts.dateStyle = "medium"; opts.timeStyle = "short"
  }
  return new Intl.DateTimeFormat(undefined, opts).format(date)
}

function defaultPlaceholder(presentation: Presentation): string {
  switch (presentation) {
    case "time":  return "Pick a time"
    case "date":  return "Pick a date"
    case "month":
    case "month-year": return "Pick a month"
    case "year":  return "Pick a year"
    default:      return "Pick date and time"
  }
}
</script>

<style scoped>
.themed-dt-field {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.themed-dt-label {
  margin-bottom: 0.375rem;
}

.themed-dt-required {
  color: var(--color-error);
  margin-left: 0.125rem;
}

.themed-dt-trigger {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.875rem 1rem;
  background-color: var(--color-surface-container-low);
  border: none;
  border-bottom: 2px solid var(--color-outline-variant);
  border-radius: 0.5rem 0.5rem 0 0;
  color: var(--color-on-surface-variant);
  font-family: 'Inter', sans-serif;
  font-size: 0.9375rem;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.15s, background-color 0.15s;
}

.themed-dt-trigger:hover,
.themed-dt-trigger:focus-visible {
  border-bottom-color: var(--color-primary);
  outline: none;
}

.themed-dt-trigger.has-value {
  color: var(--color-on-surface);
}

.themed-dt-trigger-text {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.themed-dt-trigger-icon {
  font-size: 1.25rem;
  color: var(--color-primary);
  flex-shrink: 0;
}

.themed-dt-clear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  background: transparent;
  border: none;
  border-radius: 9999px;
  color: var(--color-on-surface-variant);
  cursor: pointer;
  flex-shrink: 0;
  padding: 0;
}
.themed-dt-clear:hover {
  background-color: var(--color-surface-container-high);
  color: var(--color-on-surface);
}
.themed-dt-clear .material-symbols-rounded {
  font-size: 1rem;
}
</style>
